/**
 * Branded `Cents` integer type and conversion helpers.
 *
 * Every money number that crosses a boundary in the billing
 * pipeline should be a `Cents` (integer) — never a "$X.YZ" float.
 *
 * Why this matters
 * ----------------
 * Floats are not closed under decimal arithmetic, so even a single
 * `0.1 + 0.2` already produces `0.30000000000000004`. In a billing
 * context that rounds at every JSON boundary, dollar floats accumulate
 * sub-cent drift that surfaces as "off by one" reconciliation errors
 * months later.
 *
 * The brand makes it a compile-time error to mix `Cents` with a
 * raw `number`, so a future engineer can't accidentally do
 * `parseFloat(stripeCharge.amount_dollars) + tagadaFeeCents`.
 *
 * Usage
 * -----
 * ```ts
 * import { Cents, dollarsToCents, centsToDollars, cents } from '@tagadapay/billing-core/domain/money';
 *
 * const fee: Cents = dollarsToCents(12.34); // 1234
 * const sum: Cents = cents(fee + cents(5_00)); // 1734
 * const display = centsToDollars(sum); // 17.34
 * ```
 *
 * Notes
 * -----
 * - `cents()` ALWAYS rounds with `Math.round` (half-away-from-zero in
 *   modern JS engines for finite values). Inputs that are already
 *   integers are unchanged. Non-finite inputs throw — silent NaN
 *   propagation is exactly the bug class this brand exists to prevent.
 * - `dollarsToCents(12.34)` is exactly `1234`, not `1233.9999...`.
 *   Internally it does `Math.round(d * 100)` rather than the naive
 *   `d * 100` so float imprecision in `d` doesn't leak.
 * - `centsToDollars` returns a plain `number` because cents-as-int
 *   ÷ 100 is always exact for safe-integer cents (which is roughly
 *   every realistic invoice up to $90 trillion).
 */

declare const CENTS_BRAND: unique symbol;

/**
 * An integer count of cents. Tagged at the type level so the compiler
 * refuses to mix it with raw numbers, dollar floats, basis points,
 * etc.
 */
export type Cents = number & { readonly [CENTS_BRAND]: true };

function assertFinite(n: number, label: string): void {
    if (!Number.isFinite(n)) {
        throw new TypeError(`[billing-core/money] ${label} must be a finite number, got ${n}`);
    }
}

/**
 * Coerce an arbitrary number to `Cents`, rounding to the nearest
 * integer cent. Use this when the source is unavoidably a float
 * (e.g. ClickHouse `amount_usd`).
 */
export function cents(n: number): Cents {
    assertFinite(n, 'cents()');
    return Math.round(n) as Cents;
}

/**
 * Convert dollars (float) to `Cents` (int). The right way to do it:
 * `Math.round(d * 100)` so a value like `0.1 + 0.2 = 0.30000000000004`
 * still maps to exactly `30` cents instead of `29` or NaN.
 */
export function dollarsToCents(dollars: number): Cents {
    assertFinite(dollars, 'dollarsToCents()');
    return Math.round(dollars * 100) as Cents;
}

/**
 * Convert `Cents` back to dollars for display. Returns a plain
 * float; integer cents ÷ 100 is exact for any realistic invoice
 * size, so no precision is lost.
 */
export function centsToDollars(c: Cents): number {
    return c / 100;
}

/**
 * Sum a list of `Cents` safely. Folding with `+` would lose the
 * brand, so this helper keeps the result correctly tagged.
 */
export function sumCents(values: readonly Cents[]): Cents {
    let total = 0;
    for (const v of values) total += v;
    return total as Cents;
}

/**
 * Multiply `Cents` by a basis-points value, rounding to int cents.
 * Equivalent to `Math.round(c * bps / 10000)`.
 *
 * Domain note: bps × cents is the workhorse of every share and
 * spread computation in this package. Centralising it here means
 * we only ever round once per multiplication.
 */
export function applyBps(amount: Cents, bps: number): Cents {
    assertFinite(bps, 'applyBps(bps)');
    return Math.round((amount * bps) / 10_000) as Cents;
}

/**
 * Apply a `partnerSharePct` (0..100) to a `Cents` amount, rounded
 * to int cents. Mirrors the math in `computeDealSplit` so partner
 * shares anywhere in the codebase round identically.
 */
export function applySharePct(amount: Cents, pct: number): Cents {
    assertFinite(pct, 'applySharePct(pct)');
    return Math.round((amount * pct) / 100) as Cents;
}
